/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Tooltip, Typography, IconButton, Skeleton } from '@mui/material';
import { LuiPageHeader } from '@jda/lui-common-component-library-mui5';
import { IErrorMessage } from '../../models/Common/ErrorMessage';
import Loader from '../../shared/Loader';
import RoleList from '../../components/RoleList/RoleList';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import NoItems from '../../shared/NoItems';
import { getAllRoles, deleteRole } from '../../services/roles';
import { IRole } from '../../models/Roles/RolesContracts';
import { useStyles } from './styles';
import AddRoleDialog from '../../components/AddRoleDialog';
import RoleDetails from '../../components/RoleDetails';
import GlobalRoleDetails from '../../components/GlobalRoleDetails/GlobalRoleDetails';
import { NO_RESULT_FOUND, PLEASE_TRY_A_DIFFERENT_ROLE } from '../constants';
import { CheckViewPermission, CheckResourcePermissions } from '../../shared/CheckPermissions/CheckViewPermission';
import { actions, permissions, resources } from '../../shared/CheckPermissions/constants';
import DeleteBtn from '../../shared/DeleteBtn/DeleteBtn';
import {
  DELETE_ROLE_TITLE,
  DIALOGUE_OK,
  DIALOGUE_CONTENT,
  DIALOGUE_CANCEL,
  SNACKBAR_ALERT_DEFAULT_PROPS,
  DELETE_ROLE_SUCCESS,
  DELETE_ROLE_ERROR_422,
  DELETE_ROLE_ERROR,
  TITLE,
  SEARCH_ROLE,
  DIALOGUE_CONTENT_GLOBAL_ROLE,
  DELETE_GLOBAL_ROLE
} from './constants';
import { GLOBAL_ROLE_TYPE } from '../../components/RoleList/constants';
import SnackbarAlert from '../../shared/SnackbarAlert';
import interpolateString from '../../utility/stringInterpolation';
import { Edit, FileExportAll, PlusCircle } from '@jda/lui-common-icon-library-mui5';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllFilteredRoles,
  selectNoRole,
  selectRoles,
  selectRoleSearch,
  selectSelectedRoleItem
} from '../../store/role/roleSelector';
import {
  getRoleListAction,
  resetRolesState,
  setFilteredRoleAction,
  setHasNoRolesAction,
  setRoleSearchValue,
  setSelectedRoleAction
} from '../../store/role/roleActionCreator';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import CustomSearch from '../../components/CustomSearch';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';

import { exportRoles } from '../../store/role/role';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';

const Roles = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });
  const [showLoader, showHideLoader] = useState(false);
  const [openModal, openCloseModal] = React.useState(false);
  const [roleName, setRoleName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<IRole[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isDescription, setDescription] = useState('');
  const searchValue = useSelector(selectRoleSearch);
  const roleList = useSelector(selectRoles);
  const hasNoRolesItems = useSelector(selectNoRole);
  const selectedItem = useSelector(selectSelectedRoleItem);
  const filteredRoles = useSelector(selectAllFilteredRoles);
  const appLocation = useSelector(selectAppLocation);
  const isExportRolesEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.exportRoles);

  const onSearchTextBoxChange = (searchValue: string) => {
    dispatch(setRoleSearchValue(searchValue));
  };

  const filterBySearchValue = useCallback(() => {
    const rolesCopy = [...roleList];
    if (searchValue !== '') {
      const filteredRoles = rolesCopy.filter(
        role => role.name && role.name.toUpperCase().includes(searchValue.toUpperCase())
      );
      dispatch(setFilteredRoleAction(filteredRoles));
      const newRole = filteredRoles.filter(r => r.name === searchValue)[0];
      const selectRole = newRole ? newRole : filteredRoles[0];
      dispatch(setSelectedRoleAction(selectRole));
      if (selectRole) {
        setRoleName(selectRole.name);
        setDescription(
          selectRole.description.length > 50
            ? selectRole.description.substring(0, 50).concat('...')
            : selectRole.description.substring(0, 50)
        );
      } else {
        setDescription('');
      }
      dispatch(setHasNoRolesAction(filteredRoles.length === 0));
    } else {
      dispatch(setFilteredRoleAction(roleList));
      if (roleList[0]) {
        setRoleName(roleList[0].name);
        setDescription(
          roleList[0].description.length > 50
            ? roleList[0].description.substring(0, 50).concat('...')
            : roleList[0].description.substring(0, 50)
        );
      }
      dispatch(setSelectedRoleAction(roleList[0]));
      dispatch(setHasNoRolesAction(false));
    }
  }, [dispatch, searchValue, roleList]);

  const getRoles = useCallback(
    (newRole: string) => {
      showHideLoader(true);
      getAllRoles(appLocation?.name)
        .then((response: IRole[]) => {
          response
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((item: IRole) => (item.priority = Math.floor(Math.random() * 5)));

          const updatedResponse = response.map((item: IRole) => ({
            ...item,
            isChecked: false
          }));

          dispatch(getRoleListAction(updatedResponse));
          showHideLoader(false);
          if (updatedResponse.length === 0) {
            dispatch(setHasNoRolesAction(true));
          } else {
            if (newRole !== '') {
              dispatch(setRoleSearchValue(newRole));
            }
          }
        })
        .catch(_error => {
          const errorMessage = {
            message: interpolateString([TITLE])
          };
          setErrors(errorMessage);
        });
    },
    [dispatch, appLocation]
  );

  useEffect(() => {
    getRoles('');
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        showSnackbar: false
      })
    );
  }, [dispatch, getRoles]);

  useEffect(() => {
    filterBySearchValue();
  }, [filterBySearchValue]);

  useEffect(() => {
    return () => {
      dispatch(resetRolesState());
    };
  }, [dispatch]);

  const handleListItemClick = (_event: React.MouseEvent<HTMLDivElement, MouseEvent>, listItem: IRole) => {
    dispatch(setSelectedRoleAction(listItem));
    setRoleName(listItem.name);
    if (listItem.description) {
      setDescription(
        listItem.description.length > 50
          ? listItem.description.substring(0, 50).concat('...')
          : listItem.description.substring(0, 50)
      );
    }
  };

  const handleListItemCheck = (_event: any, listItem: IRole) => {
    const updatedRoles = filteredRoles.map((role: IRole) =>
      role.id === listItem.id && role.type !== 'global' ? { ...role, isChecked: !role.isChecked } : role
    );
    dispatch(setFilteredRoleAction(updatedRoles));

    const checkedRoles = updatedRoles.filter((role: IRole) => role.isChecked);
    setSelectedRoles(checkedRoles);
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        message: message,
        severity: severity
      })
    );
  };

  const hideSnackbar = () => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        showSnackbar: false
      })
    );
  };
  const editRole = () => {
    setIsEdit(true);
    openCloseModal(!openModal);
  };

  const addRole = () => {
    setIsEdit(false);
    openCloseModal(!openModal);
  };
  const deleteRoleItem = () => {
    dispatch(setRoleSearchValue(''));
    if (selectedRoles.length) setSelectedRoles([]);
    return deleteRole(selectedItem!.id);
  };

  const handleExportRoles = () => {
    const ids = selectedRoles.map((role: IRole) => role.id);
    exportRoles(dispatch, 'id', ids);
  };

  return (
    <CheckViewPermission
      permission={[permissions.role.read, permissions.role.read_write, permissions.role.read_assign]}
    >
      <Box className={classes.root}>
        <Grid container>
          <Grid item xs={12} sm={3}>
            <Box className={classes.topLeftHeaderContainer}>
              <Grid container>
                <Grid item xs={12} sm={12} md={10}>
                  <CustomSearch value={searchValue} onSearchChange={onSearchTextBoxChange} placeHolder={SEARCH_ROLE} />
                </Grid>
                {!appLocation && (
                  <Grid item xs={12} sm={12} md={2}>
                    <Box className={classes.roleActionItems}>
                      <CheckViewPermission permission={[permissions.role.read_write]}>
                        <PlusCircle className={classes.plusIcon} onClick={addRole} data-testid="add-role-plus-icon" />
                        {openModal && (
                          <AddRoleDialog
                            openModal={openModal}
                            openCloseModal={openCloseModal}
                            refreshOnSave={getRoles}
                            displaySnackbar={displaySnackbar}
                            hideSnackbar={hideSnackbar}
                            edit={isEdit}
                          />
                        )}
                      </CheckViewPermission>
                      {isExportRolesEnabled && (
                        <CheckResourcePermissions action={actions.EXPORT} resource={resources.REALM_ENTITIES}>
                          {selectedRoles.length > 0 && (
                            <Tooltip title={'Export to ALM'}>
                              <Box onClick={handleExportRoles} className={classes.exportButton}>
                                <FileExportAll className={classes.exportIconFile} />
                              </Box>
                            </Tooltip>
                          )}
                        </CheckResourcePermissions>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box paddingTop={0.75} className={classes.topRightHeaderContainer}>
              <Box className={classes.displayNameHeading}>
                <Typography variant="h3" data-testid="details-heading">
                  {selectedItem && selectedItem.name}
                </Typography>
                {selectedItem && (
                  <Tooltip title={selectedItem && selectedItem.description ? selectedItem.description : ''}>
                    <Typography variant="body1" display="inline" className={classes.app}>
                      {isDescription}
                    </Typography>
                  </Tooltip>
                )}
              </Box>

              {!appLocation && (
                <>
                  <CheckViewPermission permission={[permissions.role.read_write]}>
                    {!hasNoRolesItems && (
                      <Tooltip title="Edit">
                        <Box className={classes.roleEditButton} data-testid="toolTip-sign">
                          <Box id="editIcon" onClick={editRole}>
                            <IconButton aria-label="edit">
                              <Edit />
                            </IconButton>
                          </Box>
                        </Box>
                      </Tooltip>
                    )}
                  </CheckViewPermission>
                  {!hasNoRolesItems && (
                    <DeleteBtn
                      permissions={[permissions.role.read_write]}
                      onDelete={deleteRoleItem}
                      refreshOnSave={getRoles}
                      displaySnackbar={displaySnackbar}
                      hideSnackbar={hideSnackbar}
                      dialog={{
                        title:
                          selectedItem?.type === GLOBAL_ROLE_TYPE ? DIALOGUE_CONTENT_GLOBAL_ROLE : DELETE_ROLE_TITLE,
                        content: selectedItem?.type === GLOBAL_ROLE_TYPE ? DELETE_GLOBAL_ROLE : DIALOGUE_CONTENT,
                        primaryButtonText: DIALOGUE_OK,
                        secondaryButtonText: DIALOGUE_CANCEL,
                        recordName: roleName,
                        selectedRole: selectedItem?.type
                      }}
                      messages={{
                        success: interpolateString([roleName], DELETE_ROLE_SUCCESS),
                        error: {
                          unprocessableEntity: DELETE_ROLE_ERROR_422,
                          failure: DELETE_ROLE_ERROR
                        }
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container className={classes.mainContent}>
          <Grid item xs={12} sm={3}>
            <Box
              className={[
                classes.listItemPaper,
                filteredRoles && filteredRoles.length ? classes.listContainerBorder : ''
              ].join(' ')}
            >
              <Loader showLoader={showLoader} isCenter customClasses={classes.loader} />
              {filteredRoles && filteredRoles.length > 0 && selectedItem && (
                <RoleList
                  getAllRoles={getRoles}
                  filteredRoles={filteredRoles}
                  selectedItem={selectedItem}
                  handleListItemClick={handleListItemClick}
                  handleListItemCheck={handleListItemCheck}
                />
              )}
              {hasNoRolesItems && <NoItems title={NO_RESULT_FOUND} subHeading={PLEASE_TRY_A_DIFFERENT_ROLE} />}
              {errors && <ErrorMessage {...errors} />}
            </Box>
          </Grid>
          <Grid item xs={12} sm={9} className={classes.containerGid}>
            <Box p={1} className={classes.rightContainer}>
              {!(selectedItem && selectedItem.id) ? null : selectedItem.type === GLOBAL_ROLE_TYPE ? (
                <GlobalRoleDetails selectedItem={selectedItem} />
              ) : (
                <RoleDetails selectedItem={selectedItem} />
              )}
            </Box>
          </Grid>
        </Grid>
        <SnackbarAlert />
      </Box>
    </CheckViewPermission>
  );
};

const Role = {
  component: Roles,
  name: 'Roles'
};

export default Role;
